import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { ME_QUERY_KEY } from '../lib/meQuery';
import { workspaceKey } from '../lib/workspaceQueryKey';
import type { FeedbackItem, MeResponse } from '../api/types';
import { countUnreadFeedback, isFeedbackUnread, markFeedbackSeen, type FeedbackSeen } from './unread';

/** The ONE cache entry for the member's own feedback IN ONE WORKSPACE. The nav badge and
 *  the Feedback page share it — a second key would double the upstream cost below and let
 *  the two surfaces disagree about the same thread. Workspace-keyed since steward-app#845:
 *  `/feedback` is author-scoped AND workspace-scoped upstream, so the same member's threads
 *  differ between two workspaces they belong to. */
export const feedbackQueryKey = (workspaceId: string | null) => workspaceKey('feedback', workspaceId);

/**
 * `staleTime` matches `['me']`. It is not cosmetic: mounting this query in
 * `Layout` runs it on every app load and every window focus, and upstream
 * appcore's `listForAuthor` drains ALL pages of the workspace FEEDBACK
 * partition and filters `_cb` in memory. Without a stale window, an alt-tab
 * would pay for a full-partition scan.
 */
const FEEDBACK_STALE_TIME = 5 * 60_000;

/** Stable empty map so `useMemo`/`useCallback` below don't re-run per render
 *  when the user has no read state yet. */
const NO_SEEN: FeedbackSeen = {};

/**
 * The member's own feedback, newest first as the façade returns it.
 *
 * Gated on a resolved workspace, not just a token, because this is the first
 * query the app mounts from `Layout` rather than from a page, so it would
 * otherwise fire with no `X-Workspace-Id` for a user who has no active
 * workspace at all — the suspended / member-less user parked on
 * `/select-workspace`, where `activeWorkspaceId` is null. There is nothing to
 * ask for in that state, and the answer would be a 403 the member can do
 * nothing about.
 *
 * Note what the gate does NOT mean: `/select-workspace` renders inside
 * `Layout`, and is also reachable *with* a healthy active workspace —
 * `WorkspaceSwitcher` navigates there directly when the user clicks a
 * co-existing suspended membership, without going through `switchWorkspace`
 * (`SelectWorkspace` itself computes `isActive` against `activeWorkspaceId`,
 * which only makes sense if it can be non-null there). So this query genuinely
 * can fire on that page. That is fine and not a loop: appcore holds the access
 * boundary, and `redirectToSelectWorkspace` in `api/client.ts` is latched and
 * no-ops when already on the page.
 */
export function useFeedbackQuery() {
  const token = useAccessToken();
  const { activeWorkspaceId } = useWorkspace();
  return useQuery({
    queryKey: feedbackQueryKey(activeWorkspaceId),
    queryFn: () => createApiClient(token!).get<{ items: FeedbackItem[] }>('/feedback').then(r => r.items),
    enabled: !!token && !!activeWorkspaceId,
    staleTime: FEEDBACK_STALE_TIME,
  });
}

export interface FeedbackUnread {
  /** How many threads carry an operator reply the member has not read. */
  count: number;
  /** Whether one specific item does. */
  isUnread: (item: FeedbackItem) => boolean;
  /** Record that the member has now read thread `id`. No-ops (no `PUT /me`)
   *  when there is nothing new in it. */
  markSeen: (id: string) => void;
}

/**
 * Composes the shared `['feedback']` list with the `feedbackSeen` preference
 * carried on `['me']` into the unread badge state. Both surfaces (the side-nav
 * item and the Feedback page rows) read from here, so they can never show
 * different counts.
 */
export function useFeedbackUnread(): FeedbackUnread {
  const { data } = useFeedbackQuery();
  const { profile, setPreferences, activeWorkspaceId } = useWorkspace();
  const queryClient = useQueryClient();

  const items = useMemo(() => data ?? [], [data]);
  const seen = profile?.preferences?.feedbackSeen ?? NO_SEEN;

  const count = useMemo(() => countUnreadFeedback(items, seen), [items, seen]);
  const isUnread = useCallback((item: FeedbackItem) => isFeedbackUnread(item, seen), [seen]);

  /**
   * Tail of the `PUT /me` chain, so a later `markSeen` can never overtake an
   * earlier one.
   *
   * Without it, expanding thread A and then thread B fires two independent
   * `PUT /me`s. Appcore replaces the whole `feedbackSeen` value top-level, so
   * if A's request (carrying only `{A}`) happens to land *after* B's (carrying
   * `{A, B}`), B's marker is silently erased and its chip and the nav count
   * come back after the member already read it. The server's
   * optimistic-concurrency retry cannot save this: it re-applies the same stale
   * body. Ordering has to be imposed here, at the only place that knows the
   * intended sequence.
   */
  const writeQueue = useRef<Promise<void>>(Promise.resolve());

  const markSeen = useCallback((id: string) => {
    writeQueue.current = writeQueue.current
      .then(async () => {
        // Read the inputs HERE, not from the render that queued this write.
        // A queued write executes after the one before it has landed, by which
        // point its own view of `feedbackSeen` is a revision behind — building
        // the patch from a closure would hand appcore exactly the stale body
        // the queue exists to prevent. The ['me'] cache is the same thing
        // `setPreferences` reads and writes optimistically, so it is current
        // the instant the previous write returns.
        const latestSeen = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.feedbackSeen;
        // ⚠️ The one input still read from the QUEUING render, and the comment above does not
        // cover it. `activeWorkspaceId` is captured when `markSeen` was built, so a switch
        // between the click and this continuation makes it the PREVIOUS family's — which is
        // the safe direction and deliberately so: the marker belongs to the thread that was
        // read, in the workspace it was read in. Keying it to the family now on screen would
        // file one family's read state under another's. The residual cost is a dropped
        // marker if that entry has since been reset, never a wrong one (steward-app#845
        // security review).
        const latestItems = queryClient.getQueryData<FeedbackItem[]>(feedbackQueryKey(activeWorkspaceId)) ?? [];
        const next = markFeedbackSeen(latestSeen, latestItems, id);
        if (!next) return; // already read, or nothing to read — don't spend a write
        // `setPreferences` updates the ['me'] cache optimistically (so the badge
        // clears at once) and reverts to server truth if the PUT fails. A failure
        // here — including appcore's 400 PREFERENCES_TOO_LARGE when the shared 8KB
        // blob is full — is therefore silent and non-fatal by construction: the
        // badge simply comes back. Read state is a convenience, never data the
        // member would lose, so it must never surface an error over the thread they
        // just opened.
        await setPreferences({ feedbackSeen: next });
      })
      // Keeps the chain alive (a rejected tail would fail every write after it)
      // and keeps the un-awaited assignment from ever becoming an unhandled
      // rejection. `setPreferences` already swallows its own errors and
      // refetches server truth; this only catches a programming fault above it.
      .catch(() => {});
  }, [queryClient, setPreferences, activeWorkspaceId]);

  return { count, isUnread, markSeen };
}
